import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  IterableDiffers,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MenuItemDefinition } from '../../../model/menu-item-definition';
import { CommandBase, MultiselectCommand } from '../../../model/command';
import { CommandDispatcher } from '../../../service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../service/factory/command-factory.service';
import { ListViewColumnFactory } from '../../../service/factory/list-view-column-factory.service';
import { MessageService } from '../../../service/message.service';
import { ListViewBase } from '../list-view.base';
import { DefaultPageSize, PagingEvent } from '../paging/paging.component';
import { LanguageService } from '../../../service/language.service';

@Component({
    selector: 'app-multiselect-list-view',
    templateUrl: './multiselect-list-view.component.html',
    styleUrls: ['./multiselect-list-view.component.scss', './../list-view/list-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiselectListViewComponent<TSource = any> extends ListViewBase<TSource> implements DoCheck, OnChanges {
  constructor(
    private readonly _commandFactory: CommandFactoryService<any, TSource>,
    private readonly _messageService: MessageService,
    private readonly _commandDispatcher: CommandDispatcher,
    private readonly _cdr: ChangeDetectorRef,
    columnFactory: ListViewColumnFactory,
    languageService: LanguageService,
    iterableDiffers: IterableDiffers
  ) {
    super(columnFactory, languageService, iterableDiffers);

    this.control = new FormControl();
    this.control.valueChanges.subscribe(value => {
      const selectableRows = this.getSelectableRows();
      if (!value && !selectableRows.every(item => item.selected)) {
        return;
      }
      selectableRows.forEach(i => (i.selected = value));
      if (value && selectableRows.length === 0) this.control.setValue(false);
    });
  }

  @Input() menuDefinition: MenuItemDefinition[];
  @Input() externalMenuCommandIds: string[] = [];
  @Input() selectAll: boolean;
  @Input() isToolbarSticky: boolean = false;
  @Input() preferencesKey: string;
  @Output() menuCommand = new EventEmitter<CommandBase>();

  getRowNumber(index: number): number {
    return this.from + index + 1;
  }

  control: FormControl;
  isActive: boolean;
  selectedItemsCount: number;
  showMenu: boolean;
  get hasMenu(): boolean {
    return this.menuDefinition && this.menuDefinition.length > 0;
  }
  from: number = 0;
  to: number = DefaultPageSize;

  get largeNumberOfItems(): boolean {
    if (this.itemsSource) return this.itemsSource.length > 100;
    else return false;
  }

  get smallNumberOfItems(): boolean {
    return this.itemsSource?.length <= 100;
  }

  getSelectedItems(): TSource[] {
    if (this.rows) return this.rows.filter(i => i.selected).map(x => x.dataSource);
    return [];
  }

  execute(commandId: string, contextId?: string) {
    this.showMenu = false;
    let items = this.getSelectedItems();
    if (items.length > 0) {
      if (this.externalMenuCommandIds?.includes(commandId)) {
        const command =
          this.control.value && this.selectedItemsCount === this.getSelectableRows().length
            ? this._commandFactory.createFilteredCommand(commandId, [], [], this.selectedItemsCount)
            : this._commandFactory.createMultiselectCommand(commandId, items, this.itemsSource, contextId);
        this.menuCommand.emit(command);
      } else {
        const command = this._commandFactory.createMultiselectCommand(commandId, items, this.itemsSource, contextId);
        this._commandDispatcher.sendCommand(command);
      }
    } else {
      this._messageService.showWarning('WARNING.NO_SELECTED_ITEMS');
    }
  }

  ngDoCheck() {
    super.ngDoCheck();
    let selection = this.getSelectedItems();
    const selectableRows = this.getSelectableRows();
    this.selectedItemsCount = selection.length;
    this.isActive = this.selectedItemsCount > 0;
    if (this.selectedItemsCount == 0) {
      this.showMenu = false;
    }
    this.control.setValue(selectableRows.length > 0 && this.selectedItemsCount === selectableRows.length);
    this._cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes['selectAll']) {
      this.control.setValue(this.selectAll);
    }
  }

  private cancelHide = false;

  onMenuMouseEnter(): void {
    this.showMenu = true;
    this.cancelHide = true;
  }

  onMenuMouseLeave(): void {
    this.cancelHide = false;
    setTimeout(() => {
      if (!this.cancelHide) {
        this.showMenu = false;
      }
    }, 200);
  }

  openMenu() {
    if (this.isActive) this.showMenu = !this.showMenu;
  }

  pagingChanged(e: PagingEvent) {
    this.from = e.from;
    this.to = e.to;
  }

  isSelectionDisabled(row: any): boolean {
    return !!(row?.disableSelection || row?.dataSource?.disableSelection);
  }

  private getSelectableRows() {
    return (this.rows || []).filter(row => !this.isSelectionDisabled(row));
  }
}
